import { Card, Text, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useMembership } from '@/auth/SessionProvider';
import { Status } from '@/components/bits';
import { placeholders } from '@/copy/placeholders';
import { useBilling, useBillingRedirect, useErrorMessage } from '@/data/hooks';
import { monthDay } from '@/lib/time';

/**
 * **THE ONE LINE ABOVE EVERY SCREEN WHILE A CLUB'S BILLING NEEDS SOMETHING** (DECISIONS 92). A new director's first
 * job is setting billing up, because until then codes and invites are refused; before this, the first they heard of
 * it was a refusal on the Groups screen. So the shell says it, everywhere, with the one button that does it.
 *
 * Org admins act on it: Set Up Billing sends them to Settings, where the plans are; a failed payment or a pause sends
 * them to Stripe's own page. A group admin can do nothing about billing, so they see only a pause, which explains why
 * families stopped seeing the club's tasks. Nothing is drawn while the switch is closed, for a comped club, or for a
 * club that is paying.
 */
export function BillingBanner() {
  const membership = useMembership();
  const billing = useBilling(!!membership);
  const redirect = useBillingRedirect();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const b = billing.data;
  if (!membership || !b?.enabled || b.comped) return null;
  const orgAdmin = membership.role === 'orgAdmin';
  const copy = placeholders.billing;

  const needsSetUp = (b.status === 'none' || b.status === 'canceled') && !b.canGrow;
  if (b.status !== 'paused' && !(orgAdmin && (needsSetUp || b.status === 'past_due'))) return null;

  const manage = async () => {
    setError(null);
    try {
      await redirect.mutateAsync('manage');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const deletesOn =
    b.status === 'paused' && b.deletesOn
      ? copy.deletesOn(`${monthDay(b.deletesOn)} ${new Date(b.deletesOn).getFullYear()}`)
      : null;

  // the 23 Oct audit, T2-9: a pause or a failed payment is set apart from the help cards by an orange rule
  const urgent = b.status === 'paused' || b.status === 'past_due';
  return (
    <div className={`billing-banner${urgent ? ' urgent' : ''}`} data-testid="billing-banner">
      <Card variant="quiet" radius="row" padding={14}>
        <div className="stack">
          <Text variant="body" tone="ink" testID="billing-banner-line">
            {b.status === 'paused'
              ? // the 23 Oct audit, T3-9: a group admin can't change billing, so their line says who can
                orgAdmin
                ? copy.paused
                : copy.pausedAskAdmin
              : b.status === 'past_due'
                ? copy.pastDue
                : copy.setUp}
          </Text>
          {deletesOn ? (
            <Text variant="bodySm" tone="muted" testID="billing-banner-deletes">
              {deletesOn}
            </Text>
          ) : null}
          {error ? <Status testID="billing-banner-error">{error}</Status> : null}
          {orgAdmin ? (
            <div className="actions">
              {needsSetUp ? (
                <WasabiButton
                  label={copy.setUpButton}
                  size="sm"
                  fullWidth={false}
                  onPress={() => navigate('/settings')}
                  testID="billing-banner-setup"
                />
              ) : (
                <WasabiButton
                  label={copy.manage}
                  size="sm"
                  fullWidth={false}
                  onPress={manage}
                  testID="billing-banner-manage"
                />
              )}
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
